import { useEffect, useRef, useState } from "react";
import { Trash2, UploadCloud, Link2, Star, Pencil, X, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, photoSrc } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SPORTS = ["Basketball", "Football", "Soccer / Football", "Track & Field", "Combat", "Motorsports", "General"];
const EMPTY = { title: "", sport: "Basketball", venue: "", iso: "", shutter: "", focal: "", license: "Editorial", album: "" };

export const PhotosTab = () => {
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState("");
  const [previews, setPreviews] = useState([]);
  const [urlMode, setUrlMode] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [uploading, setUploading] = useState(false);
  const [albumMeta, setAlbumMeta] = useState({});
  const [selected, setSelected] = useState([]);
  const [bulkFolder, setBulkFolder] = useState("");
  const [bulkNew, setBulkNew] = useState("");
  const [bulkMoving, setBulkMoving] = useState(false);
  const fileRef = useRef(null);

  const folderNames = [...new Set(photos.map((p) => p.album).filter(Boolean))];

  const load = () => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
    api
      .get("/albums")
      .then((r) => {
        const map = {};
        r.data.forEach((a) => {
          map[a.name] = a.cover_photo_id;
        });
        setAlbumMeta(map);
      })
      .catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const movePhoto = async (p, album) => {
    try {
      await api.patch(`/photos/${p.id}`, { album });
      toast.success(album ? `Moved to "${album}".` : "Removed from folder.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectAll = () =>
    setSelected((prev) => (prev.length === photos.length && photos.length > 0 ? [] : photos.map((p) => p.id)));

  const bulkMove = async () => {
    const target = bulkFolder === "__new" ? bulkNew.trim() : bulkFolder;
    if (!selected.length) return;
    if (bulkFolder === "__new" && !target) {
      toast.error("Type a name for the new folder.");
      return;
    }
    setBulkMoving(true);
    try {
      for (let i = 0; i < selected.length; i++) {
        await api.patch(`/photos/${selected[i]}`, { album: target });
      }
      toast.success(
        target
          ? `Moved ${selected.length} photos to "${target}".`
          : `Removed ${selected.length} photos from folders.`
      );
      setSelected([]);
      setBulkFolder("");
      setBulkNew("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkMoving(false);
    }
  };

  const setCover = async (p) => {
    try {
      await api.put("/albums/cover", { album: p.album, photo_id: p.id });
      toast.success(`"${p.title}" is now the folder cover.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleOutside = async (p) => {
    try {
      await api.patch(`/photos/${p.id}`, { show_outside: !p.show_outside });
      toast.success(p.show_outside ? `"${p.title}" tucked into the folder.` : `"${p.title}" now shows on the main page.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({ title: p.title, sport: p.sport, album: p.album || "", url: p.url || "", show_outside: !!p.show_outside });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const payload = { title: editForm.title, sport: editForm.sport, album: editForm.album, show_outside: editForm.show_outside };
      if (editTarget.source === "url") payload.url = editForm.url;
      await api.patch(`/photos/${editTarget.id}`, payload);
      toast.success("Photo updated.");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const pickFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith("image/")).slice(0, 20);
    if (!arr.length) return;
    setFiles(arr);
    setPreviews(arr.map((f) => URL.createObjectURL(f)));
    if (arr.length === 1 && !form.title) {
      setForm((prev) => ({ ...prev, title: arr[0].name.replace(/\.[^.]+$/, "") }));
    }
  };

  const removeFileAt = (idx) => {
    URL.revokeObjectURL(previews[idx]);
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const upload = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (urlMode) {
        if (!urlValue || !form.title) {
          toast.error("Add an image URL and a title.");
          setUploading(false);
          return;
        }
        await api.post("/photos/url", { ...form, url: urlValue });
        toast.success("Photo added to library.");
      } else {
        if (!files.length) {
          toast.error("Choose at least one image first.");
          setUploading(false);
          return;
        }
        for (let i = 0; i < files.length; i++) {
          setProgress(`Uploading ${i + 1}/${files.length}…`);
          const fd = new FormData();
          fd.append("file", files[i]);
          const meta = {
            ...form,
            title: files.length === 1 ? form.title : files[i].name.replace(/\.[^.]+$/, ""),
          };
          Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
          await api.post("/photos/upload", fd);
        }
        setProgress("");
        toast.success(
          files.length === 1
            ? "Photo uploaded to the library."
            : `${files.length} photos added to ${form.album ? `"${form.album}"` : "the library"}.`
        );
      }
      setForm(EMPTY);
      setFiles([]);
      setPreviews([]);
      setUrlValue("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.title}" from the library?`)) return;
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Photo removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const field =
    "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none h-10";

  return (
    <div data-testid="admin-photos-tab">
      <form onSubmit={upload} className="border border-white/10 bg-[#12151C] p-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">Add frame to library</p>
          <button
            type="button"
            data-testid="admin-photo-urlmode-toggle"
            onClick={() => setUrlMode(!urlMode)}
            className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400 hover:text-amber-300"
          >
            <Link2 size={13} /> {urlMode ? "Upload file instead" : "Add via URL instead"}
          </button>
        </div>

        {urlMode ? (
          <Input
            data-testid="admin-photo-url-input"
            placeholder="https://… image URL"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            className={`${field} mb-4`}
          />
        ) : (
          <div
            data-testid="admin-photo-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFiles(e.dataTransfer.files);
            }}
            className="mb-4 cursor-pointer border border-dashed border-white/20 hover:border-[#FF3B30]/70 transition-colors bg-[#0A0C10] p-6 flex items-center gap-5"
          >
            <UploadCloud size={28} className="text-slate-500 shrink-0" />
            <div>
              <p className="text-sm text-slate-300">
                {files.length === 0 && "Drop images or click to browse — select up to 20 at once"}
                {files.length === 1 && files[0].name}
                {files.length > 1 &&
                  `${files.length} images selected — all go to ${form.album ? `"${form.album}"` : "the library (no folder)"}`}
              </p>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-600 mt-1">
                JPG / PNG / WEBP · max 30MB each · stored in object storage
              </p>
            </div>
            <input
              ref={fileRef}
              data-testid="admin-photo-upload-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => pickFiles(e.target.files)}
            />
          </div>
        )}

        {files.length > 0 && !urlMode && (
          <div data-testid="upload-previews-grid" className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2 mb-4">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="relative border border-white/10 overflow-hidden bg-[#0A0C10]">
                <img src={previews[i]} alt={f.name} className="h-16 w-full object-cover" />
                <button
                  type="button"
                  data-testid={`upload-preview-remove-${i}`}
                  onClick={() => removeFileAt(i)}
                  className="absolute top-1 right-1 bg-black/70 border border-white/20 p-1 text-slate-300 hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors"
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={12} />
                </button>
                <p className="absolute bottom-0 inset-x-0 bg-black/60 text-[8px] font-mono text-slate-300 truncate px-1.5 py-0.5">
                  {f.name}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input data-testid="admin-photo-title-input" required={urlMode || files.length <= 1} placeholder={files.length > 1 ? "Titles auto-filled from filenames" : "Title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
          <select data-testid="admin-photo-sport-select" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="h-10 w-full bg-[#0A0C10] border border-white/15 px-3 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#FF3B30]">
            {SPORTS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <Input data-testid="admin-photo-album-input" placeholder="Folder / album (optional)" value={form.album} onChange={(e) => setForm({ ...form, album: e.target.value })} className={field} list="album-suggestions" />
          <datalist id="album-suggestions">
            {[...new Set(photos.map((p) => p.album).filter(Boolean))].map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <button
            data-testid="admin-photo-upload-btn"
            type="submit"
            disabled={uploading}
            className="bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2.5 transition-colors h-10"
          >
            {uploading ? progress || "Working…" : urlMode ? "Add Photo" : files.length > 1 ? `Upload ${files.length} Photos` : "Upload"}
          </button>
        </div>
      </form>

      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">{photos.length} frames live</p>
        <button
          type="button"
          data-testid="admin-photo-select-all"
          onClick={selectAll}
          className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-white transition-colors"
        >
          {selected.length === photos.length && photos.length > 0 ? "Clear selection" : "Select all"}
        </button>
      </div>

      {selected.length > 0 && (
        <div data-testid="bulk-move-bar" className="mb-4 flex flex-wrap items-center gap-3 border border-amber-400/40 bg-amber-400/5 px-4 py-3">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-400" data-testid="bulk-move-count">
            {selected.length} selected
          </p>
          <select
            data-testid="bulk-move-folder"
            value={bulkFolder}
            onChange={(e) => setBulkFolder(e.target.value)}
            className="h-9 bg-[#0A0C10] border border-white/15 px-3 text-[11px] font-mono uppercase tracking-[0.1em] text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
          >
            <option value="">No folder</option>
            {folderNames.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
            <option value="__new">+ New folder…</option>
          </select>
          {bulkFolder === "__new" && (
            <Input
              data-testid="bulk-move-new-name"
              placeholder="New folder name"
              value={bulkNew}
              onChange={(e) => setBulkNew(e.target.value)}
              className={`${field} h-9 w-48`}
            />
          )}
          <button
            type="button"
            data-testid="bulk-move-apply"
            onClick={bulkMove}
            disabled={bulkMoving}
            className="bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-[10px] font-mono uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
          >
            {bulkMoving ? "Moving…" : "Move selected"}
          </button>
          <button
            type="button"
            data-testid="bulk-move-clear"
            onClick={() => setSelected([])}
            className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((p) => (
          <div key={p.id} data-testid={`admin-photo-card-${p.id}`} className={`group relative border bg-[#12151C] overflow-hidden transition-colors ${selected.includes(p.id) ? "border-[#FF3B30]" : "border-white/10"}`}>
            <button
              type="button"
              data-testid={`admin-photo-select-${p.id}`}
              onClick={() => toggleSelect(p.id)}
              aria-label={`Select ${p.title}`}
              className={`absolute top-2 left-2 z-10 h-5 w-5 border flex items-center justify-center transition-colors ${
                selected.includes(p.id)
                  ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                  : "bg-black/60 border-white/40 text-transparent hover:border-white"
              }`}
            >
              <Check size={13} />
            </button>
            <img src={photoSrc(p)} alt={p.title} className="h-36 w-full object-cover" loading="lazy" />
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-heading font-bold uppercase tracking-tight truncate">
                    {p.title}
                    {p.album && albumMeta[p.album] === p.id && (
                      <span className="ml-2 text-[8px] font-mono text-amber-400 border border-amber-400/40 px-1.5 py-0.5 align-middle" data-testid={`admin-photo-coverbadge-${p.id}`}>
                        COVER
                      </span>
                    )}
                  </p>
                  <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-500">
                    {[p.sport, p.source === "storage" ? "object storage" : "linked"].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <button
                  data-testid={`admin-photo-edit-${p.id}`}
                  onClick={() => openEdit(p)}
                  className="p-1.5 text-slate-500 hover:text-amber-400 transition-colors shrink-0"
                  aria-label="Edit photo"
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`admin-photo-delete-${p.id}`}
                  onClick={() => remove(p)}
                  className="p-1.5 text-slate-500 hover:text-[#FF3B30] transition-colors shrink-0"
                  aria-label="Delete photo"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <select
                  data-testid={`admin-photo-folder-${p.id}`}
                  value={p.album || ""}
                  onChange={(e) => movePhoto(p, e.target.value)}
                  className="h-8 w-full bg-[#0A0C10] border border-white/15 px-2 text-[10px] font-mono uppercase tracking-[0.08em] text-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
                >
                  <option value="">No folder</option>
                  {folderNames.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                  {p.album && !folderNames.includes(p.album) && <option value={p.album}>{p.album}</option>}
                </select>
                {p.album && (
                  <button
                    data-testid={`admin-photo-outside-${p.id}`}
                    onClick={() => toggleOutside(p)}
                    title={p.show_outside ? "Showing on main page — click to tuck into folder" : "Hidden in folder — click to show on main page"}
                    className={`shrink-0 p-1.5 border transition-colors ${
                      p.show_outside
                        ? "text-amber-400 border-amber-400/60 bg-amber-400/10"
                        : "text-slate-500 border-white/10 hover:text-amber-400 hover:border-amber-400/60"
                    }`}
                  >
                    {p.show_outside ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                )}
                {p.album && albumMeta[p.album] !== p.id && (
                  <button
                    data-testid={`admin-photo-setcover-${p.id}`}
                    onClick={() => setCover(p)}
                    title="Set as folder cover"
                    className="shrink-0 p-1.5 text-slate-500 hover:text-amber-400 border border-white/10 hover:border-amber-400/60 transition-colors"
                  >
                    <Star size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent data-testid="photo-edit-modal" className="bg-[#12151C] border-white/15 text-slate-100 rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold uppercase tracking-tight text-2xl">
              Edit Photo
            </DialogTitle>
          </DialogHeader>
          {editForm && editTarget && (
            <form onSubmit={saveEdit} className="space-y-4 pt-2">
              <img src={photoSrc(editTarget)} alt="" className="h-32 w-full object-cover border border-white/10" />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Title</p>
                <Input
                  data-testid="photo-edit-title"
                  required
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className={field}
                />
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Sport</p>
                <select
                  data-testid="photo-edit-sport"
                  value={editForm.sport}
                  onChange={(e) => setEditForm({ ...editForm, sport: e.target.value })}
                  className="h-10 w-full bg-[#0A0C10] border border-white/15 px-3 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
                >
                  {SPORTS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">
                  Folder / album — type a name or pick a suggestion
                </p>
                <Input
                  data-testid="photo-edit-album"
                  value={editForm.album}
                  onChange={(e) => setEditForm({ ...editForm, album: e.target.value })}
                  className={field}
                  list="album-suggestions"
                  placeholder="No folder"
                />
              </div>
              {editForm.album.trim() && (
                <label className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 border border-white/10 px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid="photo-edit-outside"
                    checked={editForm.show_outside}
                    onChange={(e) => setEditForm({ ...editForm, show_outside: e.target.checked })}
                    className="accent-[#FF3B30] h-4 w-4"
                  />
                  Show on main page — pick any frames to feature outside the folder
                </label>
              )}
              {editTarget.source === "url" && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Image URL</p>
                  <Input
                    data-testid="photo-edit-url"
                    value={editForm.url}
                    onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                    className={field}
                  />
                </div>
              )}
              <button
                data-testid="photo-edit-save-btn"
                type="submit"
                disabled={savingEdit}
                className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
              >
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
